import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar, { ASSETS } from '../components/Navbar';
import EventLiveMode from './EventLiveMode';
import EvolutiveMatchesTab from './EvolutiveMatches';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  ArrowRight, 
  Calendar, 
  Users, 
  MapPin, 
  Heart, 
  Sparkles, 
  Star,
  FileText,
  Eye,
  User,
  Briefcase,
  Target,
  MessageCircle,
  Shield,
  Loader2,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [application, setApplication] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [events, setEvents] = useState([]);
  const [matches, setMatches] = useState([]);
  const [suggestedConnections, setSuggestedConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [activeLiveEvent, setActiveLiveEvent] = useState(null);

  useEffect(() => {
    if (location.state?.justSubmitted) {
      setJustSubmitted(true);
    }
    fetchData();
  }, [location.state]);

  const fetchData = async () => {
    try {
      const promises = [
        axios.get(`${API}/applications/me`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/questionnaire/me`, { withCredentials: true }).catch(() => ({ data: null })),
      ];

      // Only fetch these for approved users
      if (user?.status === 'approved') {
        promises.push(
          axios.get(`${API}/events/`, { withCredentials: true }).catch(() => ({ data: { invitations: [], confirmed: [], history: [] } })),
          axios.get(`${API}/events/matches/me`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/events/suggested-connections`, { withCredentials: true }).catch(() => ({ data: { connections: [] } })),
          axios.get(`${API}/events/active-live`, { withCredentials: true }).catch(() => ({ data: { has_active_event: false } }))
        );
      }

      const results = await Promise.all(promises);
      
      setApplication(results[0].data);
      setQuestionnaire(results[1].data);
      
      if (user?.status === 'approved') {
        const eventsData = results[2]?.data || {};
        setEvents({
          invitations: eventsData.invitations || [],
          confirmed: eventsData.confirmed || [],
          history: eventsData.history || []
        });
        setMatches(results[3]?.data || []);
        setSuggestedConnections(results[4]?.data?.connections || []);
        
        // Check for active live event — auto-enter live mode
        const liveData = results[5]?.data;
        if (liveData?.has_active_event) {
          setActiveLiveEvent(liveData.event_id);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Determine user's actual status
  const getUserStatus = () => {
    // Priority: user.status > application.status
    const status = user?.status || application?.status || 'draft';
    
    // Map backend status to display status
    if (status === 'draft' || status === 'questionnaire_in_progress') {
      return 'incomplete';
    }
    if (status === 'submitted' || status === 'under_review') {
      return 'under_review';
    }
    if (status === 'waitlisted') {
      return 'waitlisted';
    }
    if (status === 'rejected') {
      return 'rejected';
    }
    if (status === 'approved') {
      return 'approved';
    }
    return 'incomplete';
  };

  const userStatus = getUserStatus();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B]">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <img src={ASSETS.logo} alt="Loading..." className="w-20 h-20 logo-glow animate-pulse object-contain" />
        </div>
      </div>
    );
  }

  // Render different dashboards based on user status
  // EVENT LIVE MODE: Full-screen takeover when active event exists
  if (activeLiveEvent) {
    return (
      <EventLiveMode 
        eventId={activeLiveEvent} 
        onExit={() => {
          setActiveLiveEvent(null);
          fetchData();
        }} 
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B]">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <p className="label-premium mb-3">{t('dashboard_title')}</p>
          <h1 className="text-4xl sm:text-5xl font-serif text-[#F5F0E6]">
            {t('dashboard_hello')}, <span className="wine-gradient">{user?.name?.split(' ')[0]}</span>
          </h1>
        </div>

        {/* Conditional rendering based on status */}
        {userStatus === 'incomplete' && (
          <DashboardIncomplete 
            questionnaire={questionnaire} 
            user={user} 
            t={t} 
          />
        )}
        
        {userStatus === 'under_review' && (
          <DashboardUnderReview 
            questionnaire={questionnaire}
            user={user}
            justSubmitted={justSubmitted}
            t={t}
          />
        )}
        
        {userStatus === 'waitlisted' && (
          <DashboardWaitlisted 
            user={user}
            t={t}
          />
        )}
        
        {userStatus === 'rejected' && (
          <DashboardRejected 
            user={user}
            t={t}
          />
        )}
        
        {userStatus === 'approved' && (
          <DashboardApproved 
            user={user}
            events={events}
            matches={matches}
            suggestedConnections={suggestedConnections}
            questionnaire={questionnaire}
            t={t}
          />
        )}
      </div>
    </div>
  );
};

// ============== DASHBOARD: INCOMPLETE ==============
const DashboardIncomplete = ({ questionnaire, user, t }) => {
  const completedSteps = questionnaire?.completed_steps?.length || 0;
  const totalSteps = 8;
  const progress = Math.round((completedSteps / totalSteps) * 100);

  return (
    <div className="max-w-2xl mx-auto">
      {/* Welcome Card */}
      <div className="card-premium p-10 mb-10 text-center">
        <Sparkles className="w-16 h-16 text-[#8B1A1A] mx-auto mb-6" />
        <h2 className="text-3xl font-serif text-[#F5F0E6] mb-4">
          {t('dashboard.incomplete.title') || 'Completa tu Perfil'}
        </h2>
        <p className="text-[#8A8A8A] mb-8 max-w-md mx-auto">
          {t('dashboard.incomplete.message') || 'Para ser considerado en nuestro proceso de selección, primero debes completar tu cuestionario de compatibilidad.'}
        </p>
        
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-[#8A8A8A]">{t('dashboard.incomplete.progress') || 'Progreso'}</span>
            <span className="text-[#8B1A1A]">{progress}%</span>
          </div>
          <div className="h-2 bg-[#8B1A1A]/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#8B1A1A] to-[#8B3A42] transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-[#8A8A8A] text-sm mt-2">
            {completedSteps} de {totalSteps} {t('dashboard.incomplete.sections') || 'secciones completadas'}
          </p>
        </div>

        <Link 
          to="/apply" 
          className="btn-wine inline-flex items-center gap-3 text-lg px-10 py-4"
          data-testid="continue-application"
        >
          <FileText size={20} />
          {completedSteps > 0 
            ? (t('dashboard.incomplete.continue') || 'Continuar Cuestionario')
            : (t('dashboard.incomplete.start') || 'Comenzar Cuestionario')
          }
          <ArrowRight size={20} />
        </Link>
      </div>

      {/* Why complete message */}
      <div className="card-premium p-8">
        <h3 className="text-lg font-serif text-[#F5F0E6] mb-4">
          {t('dashboard.incomplete.why_title') || '¿Por qué completar el cuestionario?'}
        </h3>
        <ul className="space-y-4">
          {[
            t('dashboard.incomplete.why_1') || 'Nuestro equipo revisará tu perfil personalmente',
            t('dashboard.incomplete.why_2') || 'La IA analizará tu compatibilidad potencial',
            t('dashboard.incomplete.why_3') || 'Accederás a eventos exclusivos si eres aprobado',
            t('dashboard.incomplete.why_4') || 'Conocerás personas cuidadosamente seleccionadas'
          ].map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-[#8A8A8A]">
              <CheckCircle size={18} className="text-[#8B1A1A] mt-0.5 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ============== DASHBOARD: UNDER REVIEW ==============
const DashboardUnderReview = ({ questionnaire, user, justSubmitted, t }) => {
  const steps = [
    { 
      id: 'submitted', 
      title: t('dashboard.review.step1_title') || 'Cuestionario Enviado',
      desc: t('dashboard.review.step1_desc') || 'Tu perfil ha sido recibido',
      completed: true
    },
    { 
      id: 'ai_review', 
      title: t('dashboard.review.step2_title') || 'Análisis de Compatibilidad',
      desc: t('dashboard.review.step2_desc') || 'Nuestra IA evalúa tu afinidad',
      completed: true
    },
    { 
      id: 'human_review', 
      title: t('dashboard.review.step3_title') || 'Revisión del Equipo',
      desc: t('dashboard.review.step3_desc') || 'Revisión personalizada en curso',
      completed: false,
      active: true
    },
    { 
      id: 'decision', 
      title: t('dashboard.review.step4_title') || 'Decisión Final',
      desc: t('dashboard.review.step4_desc') || 'Te notificaremos pronto',
      completed: false
    }
  ];

  return (
    <div className="max-w-3xl mx-auto">
      {/* Main Status Card */}
      <div className="card-premium p-10 mb-10">
        <div className="flex items-start gap-6">
          <div className="w-16 h-16 rounded-full bg-[#8B1A1A]/20 flex items-center justify-center flex-shrink-0">
            <Clock className="w-8 h-8 text-[#8B1A1A]" />
          </div>
          <div>
            <h2 className="text-3xl font-serif text-[#F5F0E6] mb-3">
              {t('dashboard.review.title') || 'Tu Aplicación Está en Revisión'}
            </h2>
            <p className="text-[#8A8A8A] text-lg">
              {t('dashboard.review.message') || 'Nuestro equipo está evaluando tu perfil y afinidad con la experiencia Soul Mate Dates. Este proceso puede tomar de 24 a 72 horas.'}
            </p>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="card-premium p-8 mb-10">
        <h3 className="text-xl font-serif text-[#F5F0E6] mb-8">
          {t('dashboard.review.timeline') || 'Estado del Proceso'}
        </h3>
        <div className="space-y-0">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-start gap-4">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                  step.completed 
                    ? 'bg-[#8B1A1A] border-[#8B1A1A]' 
                    : step.active 
                      ? 'border-[#8B1A1A] bg-[#8B1A1A]/20' 
                      : 'border-[#8B1A1A]/30 bg-transparent'
                }`}>
                  {step.completed ? (
                    <CheckCircle size={20} className="text-[#F5F0E6]" />
                  ) : step.active ? (
                    <Loader2 size={20} className="text-[#8B1A1A] animate-spin" />
                  ) : (
                    <span className="text-[#8B1A1A]/50 text-sm">{index + 1}</span>
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-0.5 h-12 ${
                    step.completed ? 'bg-[#8B1A1A]' : 'bg-[#8B1A1A]/20'
                  }`} />
                )}
              </div>
              <div className="pb-10">
                <h4 className={`font-medium ${step.completed || step.active ? 'text-[#F5F0E6]' : 'text-[#8A8A8A]'}`}>
                  {step.title}
                </h4>
                <p className="text-[#8A8A8A] text-sm">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Profile Summary (read-only) */}
      {questionnaire && (
        <div className="card-premium p-8">
          <h3 className="text-xl font-serif text-[#F5F0E6] mb-6 flex items-center gap-3">
            <User size={20} className="text-[#8B1A1A]" />
            {t('dashboard.review.your_profile') || 'Tu Perfil'}
          </h3>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-[#8A8A8A] text-sm mb-1">{t('dashboard.review.name') || 'Nombre'}</p>
              <p className="text-[#F5F0E6]">{questionnaire.basic_info?.name || user?.name}</p>
            </div>
            <div>
              <p className="text-[#8A8A8A] text-sm mb-1">{t('dashboard.review.city') || 'Ciudad'}</p>
              <p className="text-[#F5F0E6]">{questionnaire.basic_info?.city || '-'}</p>
            </div>
            <div>
              <p className="text-[#8A8A8A] text-sm mb-1">{t('dashboard.review.intention') || 'Intención'}</p>
              <p className="text-[#F5F0E6]">{questionnaire.intention?.looking_for || '-'}</p>
            </div>
            <div>
              <p className="text-[#8A8A8A] text-sm mb-1">{t('dashboard.review.lifestyle') || 'Estilo de Vida'}</p>
              <p className="text-[#F5F0E6]">{questionnaire.lifestyle?.lifestyle_type || '-'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== DASHBOARD: WAITLISTED ==============
const DashboardWaitlisted = ({ user, t }) => {
  return (
    <div className="max-w-2xl mx-auto">
      <div className="card-premium p-10 text-center">
        <div className="w-20 h-20 rounded-full bg-amber-900/20 flex items-center justify-center mx-auto mb-8">
          <Clock className="w-10 h-10 text-amber-400" />
        </div>
        
        <h2 className="text-3xl font-serif text-[#F5F0E6] mb-4">
          {t('dashboard.waitlist.title') || 'Estás en Lista de Espera'}
        </h2>
        
        <p className="text-[#8A8A8A] text-lg mb-8 max-w-md mx-auto">
          {t('dashboard.waitlist.message') || 'Gracias por tu interés en Soul Mate Dates. Actualmente estás en nuestra lista de espera para acceso exclusivo. Te contactaremos cuando haya disponibilidad.'}
        </p>

        <div className="card-premium bg-[#0B0B0B] p-6 text-left mb-8">
          <h3 className="text-lg font-serif text-[#F5F0E6] mb-4">
            {t('dashboard.waitlist.what_means') || '¿Qué significa esto?'}
          </h3>
          <ul className="space-y-3 text-[#8A8A8A]">
            <li className="flex items-start gap-3">
              <Shield size={18} className="text-[#8B1A1A] mt-0.5 flex-shrink-0" />
              {t('dashboard.waitlist.exclusive') || 'Mantenemos el acceso limitado para garantizar la calidad de la experiencia'}
            </li>
            <li className="flex items-start gap-3">
              <Heart size={18} className="text-[#8B1A1A] mt-0.5 flex-shrink-0" />
              {t('dashboard.waitlist.notify') || 'Te notificaremos por email cuando se abra un espacio para ti'}
            </li>
            <li className="flex items-start gap-3">
              <Sparkles size={18} className="text-[#8B1A1A] mt-0.5 flex-shrink-0" />
              {t('dashboard.waitlist.priority') || 'Tu perfil ya fue evaluado y está listo para cuando haya disponibilidad'}
            </li>
          </ul>
        </div>

        <p className="text-[#8A8A8A] text-sm">
          {t('dashboard.waitlist.questions') || '¿Tienes preguntas?'}{' '}
          <a href="mailto:hello@soulmatedates.com" className="text-[#8B1A1A] hover:underline">
            {t('dashboard.waitlist.contact') || 'Contáctanos'}
          </a>
        </p>
      </div>
    </div>
  );
};

// ============== DASHBOARD: REJECTED ==============
const DashboardRejected = ({ user, t }) => {
  return (
    <div className="max-w-2xl mx-auto">
      <div className="card-premium p-10 text-center">
        <div className="w-20 h-20 rounded-full bg-red-900/20 flex items-center justify-center mx-auto mb-8">
          <XCircle className="w-10 h-10 text-red-400" />
        </div>
        
        <h2 className="text-3xl font-serif text-[#F5F0E6] mb-4">
          {t('dashboard.rejected.title') || 'Aplicación No Aprobada'}
        </h2>
        
        <p className="text-[#8A8A8A] text-lg mb-8 max-w-md mx-auto">
          {t('dashboard.rejected.message') || 'Gracias por tu interés en Soul Mate Dates. Después de revisar tu perfil, hemos determinado que no es el momento adecuado para tu ingreso a nuestra comunidad.'}
        </p>

        <div className="card-premium bg-[#0B0B0B] p-6 text-left">
          <p className="text-[#8A8A8A]">
            {t('dashboard.rejected.note') || 'Esto no significa que no puedas aplicar en el futuro. Las circunstancias cambian y siempre estamos buscando personas comprometidas con conexiones auténticas.'}
          </p>
        </div>
      </div>
    </div>
  );
};

// ============== DASHBOARD: APPROVED ==============
const DashboardApproved = ({ user, events, matches, suggestedConnections, questionnaire, t }) => {
  const [activeTab, setActiveTab] = useState('connections');

  return (
    <div>
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-[#8B1A1A]/20 to-transparent border border-[#8B1A1A]/30 p-8 mb-10">
        <div className="flex items-center gap-4">
          <CheckCircle className="w-8 h-8 text-emerald-400 flex-shrink-0" />
          <div>
            <h2 className="text-xl font-serif text-[#F5F0E6]">
              {t('dashboard.approved.welcome') || '¡Bienvenido a Soul Mate Dates!'}
            </h2>
            <p className="text-[#8A8A8A]">
              {t('dashboard.approved.message') || 'Tu aplicación ha sido aprobada. Ahora tienes acceso a experiencias exclusivas y conexiones curadas.'}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-6 mb-10 border-b border-[#8B1A1A]/10 overflow-x-auto">
        {[
          { id: 'connections', label: t('dashboard.tabs.connections') || 'Conexiones Sugeridas', icon: Heart },
          { id: 'events', label: t('dashboard.tabs.events') || 'Mis Eventos', icon: Calendar },
          { id: 'matches', label: t('dashboard.tabs.matches') || 'Mis Matches', icon: Users },
          { id: 'profile', label: t('dashboard.tabs.profile') || 'Mi Perfil', icon: User },
          { id: 'experience', label: t('dashboard.tabs.experience') || 'Mi Experiencia', icon: Sparkles }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 pb-4 px-2 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-[#8B1A1A] text-[#8B1A1A]'
                : 'border-transparent text-[#8A8A8A] hover:text-[#F5F0E6]'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'connections' && (
        <SuggestedConnectionsTab connections={suggestedConnections} t={t} />
      )}
      
      {activeTab === 'events' && (
        <EventsTab events={events} t={t} />
      )}
      
      {activeTab === 'matches' && (
        <EvolutiveMatchesTab matches={matches} />
      )}
      
      {activeTab === 'profile' && (
        <ProfileTab user={user} questionnaire={questionnaire} t={t} />
      )}
      
      {activeTab === 'experience' && (
        <ExperienceTab user={user} t={t} />
      )}
    </div>
  );
};

// ============== SUGGESTED CONNECTIONS TAB ==============
const SuggestedConnectionsTab = ({ connections, t }) => {
  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-serif text-[#F5F0E6] mb-3">
          {t('dashboard.connections.title') || 'Conexiones Seleccionadas para Ti'}
        </h2>
        <p className="text-[#8A8A8A]">
          {t('dashboard.connections.subtitle') || 'Estas personas han sido cuidadosamente seleccionadas por su compatibilidad contigo. Podrás conocerlas en próximas experiencias presenciales.'}
        </p>
      </div>

      {connections.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {connections.slice(0, 7).map((connection, index) => (
            <ConnectionCard key={index} connection={connection} t={t} />
          ))}
        </div>
      ) : (
        <div className="card-premium p-16 text-center">
          <Heart className="w-16 h-16 text-[#8B1A1A] mx-auto mb-6 opacity-50" />
          <h3 className="text-2xl font-serif text-[#F5F0E6] mb-4">
            {t('dashboard.connections.empty_title') || 'Preparando tus Conexiones'}
          </h3>
          <p className="text-[#8A8A8A] max-w-md mx-auto">
            {t('dashboard.connections.empty_message') || 'Estamos analizando tu perfil para encontrar personas con alta compatibilidad. Pronto verás aquí tus conexiones sugeridas.'}
          </p>
        </div>
      )}

      {/* Philosophy note */}
      <div className="mt-12 p-8 border border-[#8B1A1A]/10">
        <p className="text-[#8A8A8A] text-sm italic text-center">
          "{t('dashboard.connections.philosophy') || 'No mostramos un catálogo infinito de perfiles. Cada conexión ha sido seleccionada pensando en la calidad de la experiencia, no en la cantidad.'}"
        </p>
      </div>
    </div>
  );
};

const ConnectionCard = ({ connection, t }) => {
  const { user: connectionUser, compatibility } = connection;
  
  // Affinity phrases based on score
  const getAffinityPhrase = (score) => {
    if (score >= 80) return t('dashboard.connections.affinity_high') || 'Alta compatibilidad para experiencias curadas';
    if (score >= 65) return t('dashboard.connections.affinity_medium') || 'Comparten intención de conexión profunda';
    return t('dashboard.connections.affinity_base') || 'Afinidad en estilo de vida y valores';
  };

  return (
    <div className="card-premium p-6 group hover:border-[#8B1A1A]/50 transition-all" data-testid={`connection-${connectionUser?.user_id}`}>
      {/* Header */}
      <div className="flex items-center gap-4 mb-5">
        {connectionUser?.picture ? (
          <img 
            src={connectionUser.picture} 
            alt={connectionUser.name}
            className="w-16 h-16 rounded-full border-2 border-[#8B1A1A]/30 object-cover"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#8B1A1A]/20 to-[#8B1A1A]/5 border-2 border-[#8B1A1A]/30 flex items-center justify-center">
            <span className="text-[#8B1A1A] text-xl font-serif">
              {connectionUser?.name?.charAt(0)?.toUpperCase()}
            </span>
          </div>
        )}
        <div>
          <h3 className="text-lg font-serif text-[#F5F0E6] group-hover:text-[#8B1A1A] transition-colors">
            {connectionUser?.name?.split(' ')[0]}
          </h3>
          <p className="text-[#8A8A8A] text-sm">
            {connectionUser?.age && `${connectionUser.age} años`}
            {connectionUser?.city && ` • ${connectionUser.city}`}
          </p>
        </div>
      </div>

      {/* Info */}
      {connectionUser?.occupation && (
        <p className="text-[#8A8A8A] text-sm mb-4 flex items-center gap-2">
          <Briefcase size={14} className="text-[#8B1A1A]" />
          {connectionUser.occupation}
        </p>
      )}

      {/* Affinity */}
      <div className="p-4 bg-[#8B1A1A]/5 border border-[#8B1A1A]/10 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={14} className="text-[#8B1A1A]" />
          <span className="text-[#8B1A1A] text-xs font-medium uppercase tracking-wider">
            {t('dashboard.connections.affinity') || 'Afinidad'}
          </span>
        </div>
        <p className="text-[#F5F0E6] text-sm">
          {getAffinityPhrase(compatibility?.score || 60)}
        </p>
      </div>

      {/* Compatibility reasons */}
      {compatibility?.top_reasons?.length > 0 && (
        <ul className="space-y-2">
          {compatibility.top_reasons.slice(0, 2).map((reason, i) => (
            <li key={i} className="text-[#8A8A8A] text-xs flex items-center gap-2">
              <CheckCircle size={12} className="text-emerald-500" />
              {reason}
            </li>
          ))}
        </ul>
      )}

      {/* Footer */}
      <div className="mt-5 pt-4 border-t border-[#8B1A1A]/10">
        <p className="text-[#8A8A8A] text-xs text-center">
          {t('dashboard.connections.meet_at_event') || 'Podrás conocerla en próximas experiencias'}
        </p>
      </div>
    </div>
  );
};

// ============== EVENTS TAB ==============
const EventsTab = ({ events, t }) => {
  const [rsvpLoading, setRsvpLoading] = useState(null);
  const [selectionConfig, setSelectionConfig] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [submittingSelections, setSubmittingSelections] = useState(false);
  const [selectionResult, setSelectionResult] = useState(null);
  
  const handleRSVP = async (eventId, response) => {
    setRsvpLoading(eventId);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/events/${eventId}/rsvp`,
        { response },
        { withCredentials: true }
      );
      // Reload page to refresh events
      window.location.reload();
    } catch (error) {
      console.error('RSVP error:', error);
      alert(error.response?.data?.detail || 'Error al responder');
    } finally {
      setRsvpLoading(null);
    }
  };

  const loadSelectionConfig = async (eventId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/events/${eventId}/selection-config`,
        { withCredentials: true }
      );
      setSelectionConfig(response.data);
      setSelectedUsers([]);
      setSelectionResult(null);
    } catch (error) {
      console.error('Error loading selection config:', error);
      alert(error.response?.data?.detail || 'Error al cargar configuración');
    }
  };

  const toggleUserSelection = (userId) => {
    if (!selectionConfig) return;
    
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      if (selectedUsers.length < selectionConfig.max_selections) {
        setSelectedUsers([...selectedUsers, userId]);
      }
    }
  };

  const submitSelections = async () => {
    if (!selectionConfig || selectedUsers.length === 0) return;
    
    setSubmittingSelections(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/events/${selectionConfig.event_id}/select-connections`,
        { selected_user_ids: selectedUsers },
        { withCredentials: true }
      );
      setSelectionResult(response.data);
    } catch (error) {
      console.error('Submit selections error:', error);
      alert(error.response?.data?.detail || 'Error al enviar selecciones');
    } finally {
      setSubmittingSelections(false);
    }
  };

  const invitations = events?.invitations || [];
  const confirmed = events?.confirmed || [];
  const history = events?.history || [];
  
  // Filter completed events where user attended and can make selections
  const completedWithSelections = history.filter(e => 
    e.invitation_status === 'accepted' && e.status === 'completed'
  );
  
  const hasEvents = invitations.length > 0 || confirmed.length > 0 || history.length > 0;

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-serif text-[#F5F0E6] mb-3">
          {t('dashboard.events.title') || 'Mis Eventos'}
        </h2>
        <p className="text-[#8A8A8A]">
          {t('dashboard.events.subtitle') || 'Experiencias exclusivas diseñadas para conexiones auténticas.'}
        </p>
      </div>

      {hasEvents ? (
        <div className="space-y-10">
          {/* FASE 4: Completed Events with Selection */}
          {completedWithSelections.length > 0 && !selectionConfig && (
            <div>
              <h3 className="text-lg font-serif text-[#F5F0E6] mb-4 flex items-center gap-2">
                <Heart size={20} className="text-[#8B1A1A]" />
                Selecciona tus Conexiones
              </h3>
              <p className="text-sm text-[#8A8A8A] mb-4">
                Has asistido a estos eventos. Selecciona las personas con las que te gustaría conectar.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {completedWithSelections.map(event => (
                  <div key={event.event_id} className="card-premium p-6 border-[#8B1A1A]/30">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="label-premium">{event.event_type}</span>
                      <span className="text-xs px-2 py-1 bg-emerald-900/30 text-emerald-400 border border-emerald-500/30">
                        Completado
                      </span>
                    </div>
                    <h4 className="text-lg font-serif text-[#F5F0E6] mb-3">{event.title}</h4>
                    <p className="text-[#8A8A8A] text-sm mb-4 line-clamp-2">{event.description}</p>
                    <button
                      onClick={() => loadSelectionConfig(event.event_id)}
                      className="w-full btn-wine flex items-center justify-center gap-2"
                      data-testid={`select-connections-${event.event_id}`}
                    >
                      <Heart size={16} />
                      Seleccionar Conexiones
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Selection Modal/Section */}
          {selectionConfig && !selectionConfig.already_submitted && !selectionResult && (
            <div className="card-premium p-8 border-[#8B1A1A]/50">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-serif text-[#F5F0E6] mb-2">
                    Selecciona tus Conexiones
                  </h3>
                  <p className="text-sm text-[#8A8A8A]">
                    {selectionConfig.ui_suggestion}
                  </p>
                </div>
                <button 
                  onClick={() => setSelectionConfig(null)}
                  className="text-[#8A8A8A] hover:text-white"
                >
                  <XCircle size={24} />
                </button>
              </div>

              <div className="bg-[#8B1A1A]/10 border border-[#8B1A1A]/20 p-4 mb-6">
                <p className="text-sm text-[#F5F0E6]">
                  <strong>Regla:</strong> {selectionConfig.selection_rule}
                </p>
                <p className="text-xs text-[#8A8A8A] mt-1">
                  Seleccionados: {selectedUsers.length} / {selectionConfig.max_selections}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {selectionConfig.other_attendees?.map(attendee => (
                  <div 
                    key={attendee.user_id}
                    onClick={() => toggleUserSelection(attendee.user_id)}
                    className={`p-4 border cursor-pointer transition-all ${
                      selectedUsers.includes(attendee.user_id)
                        ? 'bg-[#8B1A1A]/20 border-[#8B1A1A] ring-2 ring-[#8B1A1A]/50'
                        : 'bg-[#111] border-[#333] hover:border-[#8B1A1A]/50'
                    }`}
                    data-testid={`attendee-${attendee.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold ${
                        selectedUsers.includes(attendee.user_id) ? 'bg-[#8B1A1A]' : 'bg-[#333]'
                      }`}>
                        {attendee.first_name?.[0] || attendee.name?.[0] || '?'}
                      </div>
                      <div className="flex-1">
                        <p className="text-[#F5F0E6] font-medium">{attendee.first_name || attendee.name}</p>
                        <p className="text-xs text-[#8A8A8A]">
                          {attendee.occupation} • {attendee.age} años
                        </p>
                      </div>
                      {selectedUsers.includes(attendee.user_id) && (
                        <CheckCircle className="text-[#8B1A1A]" size={24} />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={submitSelections}
                disabled={selectedUsers.length === 0 || submittingSelections}
                className={`w-full btn-wine flex items-center justify-center gap-2 ${
                  selectedUsers.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                data-testid="submit-selections"
              >
                {submittingSelections ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <>
                    <Heart size={18} />
                    Enviar {selectedUsers.length} Selección{selectedUsers.length !== 1 ? 'es' : ''}
                  </>
                )}
              </button>
            </div>
          )}

          {/* Selection already submitted */}
          {selectionConfig && selectionConfig.already_submitted && (
            <div className="card-premium p-8 border-emerald-500/30 text-center">
              <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
              <h3 className="text-xl font-serif text-[#F5F0E6] mb-2">
                Selecciones Enviadas
              </h3>
              <p className="text-[#8A8A8A] mb-4">
                {selectionConfig.message}
              </p>
              <p className="text-sm text-emerald-400">
                Enviaste {selectionConfig.selections_count} selección{selectionConfig.selections_count !== 1 ? 'es' : ''}
              </p>
              <button
                onClick={() => setSelectionConfig(null)}
                className="mt-4 text-[#8A8A8A] hover:text-white underline"
              >
                Cerrar
              </button>
            </div>
          )}

          {/* Selection Result */}
          {selectionResult && (
            <div className="card-premium p-8 border-[#8B1A1A]/50 text-center">
              {selectionResult.matches_found > 0 ? (
                <>
                  <div className="w-20 h-20 bg-[#8B1A1A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-10 h-10 text-[#8B1A1A]" />
                  </div>
                  <h3 className="text-2xl font-serif text-[#F5F0E6] mb-2">
                    ¡Tienes {selectionResult.matches_found} Match{selectionResult.matches_found !== 1 ? 'es' : ''}!
                  </h3>
                  <p className="text-[#8A8A8A] mb-6">
                    {selectionResult.matches_found === 1 
                      ? 'Hay una conexión mutua esperándote'
                      : 'Tienes conexiones mutuas esperándote'}
                  </p>
                  <div className="space-y-3 mb-6">
                    {selectionResult.matches?.map(match => (
                      <div key={match.match_id} className="bg-[#8B1A1A]/10 border border-[#8B1A1A]/30 p-4">
                        <p className="text-[#F5F0E6] font-medium">
                          {match.matched_with.name || match.matched_with.first_name}
                        </p>
                        <p className="text-xs text-[#C4A76B]">Conexión mutua</p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                  <h3 className="text-xl font-serif text-[#F5F0E6] mb-2">
                    Selecciones Enviadas
                  </h3>
                  <p className="text-[#8A8A8A] mb-4">
                    Tus {selectionResult.selections_count} selecciones fueron guardadas exitosamente.
                  </p>
                  <p className="text-sm text-[#C4A76B]">
                    Te notificaremos si alguna de tus selecciones resulta en un match mutuo.
                  </p>
                </>
              )}
              <button
                onClick={() => {
                  setSelectionConfig(null);
                  setSelectionResult(null);
                  window.location.reload();
                }}
                className="mt-6 btn-wine"
              >
                {selectionResult.matches_found > 0 ? 'Ver Mis Matches' : 'Continuar'}
              </button>
            </div>
          )}

          {/* Pending Invitations */}
          {invitations.length > 0 && (
            <div>
              <h3 className="text-lg font-serif text-[#F5F0E6] mb-4 flex items-center gap-2">
                <Clock size={20} className="text-[#8B1A1A]" />
                {t('dashboard.events.pending_invitations') || 'Invitaciones Pendientes'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {invitations.map(event => (
                  <EventInvitationCard 
                    key={event.event_id} 
                    event={event} 
                    onRSVP={handleRSVP}
                    loading={rsvpLoading === event.event_id}
                    t={t} 
                  />
                ))}
              </div>
            </div>
          )}

          {/* Confirmed Events */}
          {confirmed.length > 0 && (
            <div>
              <h3 className="text-lg font-serif text-[#F5F0E6] mb-4 flex items-center gap-2">
                <CheckCircle size={20} className="text-emerald-500" />
                {t('dashboard.events.confirmed') || 'Eventos Confirmados'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {confirmed.map(event => (
                  <EventCard key={event.event_id} event={event} confirmed={true} t={t} />
                ))}
              </div>
            </div>
          )}

          {/* History */}
          {history.length > 0 && (
            <div>
              <h3 className="text-lg font-serif text-[#F5F0E6] mb-4 flex items-center gap-2">
                <Calendar size={20} className="text-[#8A8A8A]" />
                {t('dashboard.events.history') || 'Historial'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {history.map(event => (
                  <EventCard key={event.event_id} event={event} t={t} />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card-premium p-16 text-center">
          <Calendar className="w-16 h-16 text-[#8B1A1A] mx-auto mb-6 opacity-50" />
          <h3 className="text-2xl font-serif text-[#F5F0E6] mb-4">
            {t('dashboard.events.empty_title') || 'Próximamente'}
          </h3>
          <p className="text-[#8A8A8A] max-w-md mx-auto">
            {t('dashboard.events.empty_message') || 'Estamos preparando experiencias exclusivas para ti. Te notificaremos cuando tengas una invitación.'}
          </p>
        </div>
      )}
    </div>
  );
};

// Event Invitation Card with RSVP buttons - PREMIUM VERSION
const EventInvitationCard = ({ event, onRSVP, loading, t }) => {
  const eventDate = new Date(event.date);
  const capacityRemaining = (event.max_capacity || 0) - (event.current_capacity || 0);
  const invitationHeadline = event.compatibility_context?.invitation_headline || 
    "Seleccionado especialmente para esta experiencia exclusiva";
  const matchReasons = event.compatibility_context?.match_reasons || [];

  // Generate contextual subtitle based on event type
  const getEventContextSubtitle = () => {
    const type = event.event_type?.toLowerCase();
    if (type === 'dinner') {
      return "Este encuentro fue diseñado para perfiles con alta afinidad en estilo de vida y apertura a conexiones significativas en entornos íntimos.";
    } else if (type === 'experience') {
      return "Una experiencia curada para personas que valoran las conexiones auténticas y comparten la disposición de vivir momentos memorables.";
    } else if (type === 'cocktail') {
      return "Un ambiente sofisticado diseñado para profesionales que buscan expandir sus círculos con personas afines a su visión de vida.";
    }
    return "Una experiencia exclusiva diseñada para conectar personas con valores y aspiraciones compatibles.";
  };

  return (
    <div className="card-premium p-6 border-[#8B1A1A]/30" data-testid={`invitation-${event.event_id}`}>
      {/* Invitation badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="label-premium">{event.event_type}</span>
        </div>
        <span className="text-xs px-3 py-1 bg-[#8B1A1A]/20 text-[#8B1A1A] border border-[#8B1A1A]/30">
          {t('dashboard.events.invitation') || 'Invitación'}
        </span>
      </div>
      
      <h3 className="text-xl font-serif text-[#F5F0E6] mb-3">
        {event.title}
      </h3>
      <p className="text-[#8A8A8A] text-sm mb-4 line-clamp-2">{event.description}</p>
      
      {/* Premium Invitation Reason */}
      <div className="bg-[#8B1A1A]/10 border border-[#8B1A1A]/20 p-4 mb-5 rounded-sm">
        <p className="text-sm font-medium text-[#F5F0E6] flex items-center gap-2 mb-2">
          <Sparkles size={14} className="text-[#8B1A1A]" />
          Por qué fuiste seleccionado
        </p>
        <p className="text-sm text-[#C4A76B] italic mb-3">{invitationHeadline}</p>
        
        {/* Premium contextual subtitle */}
        <p className="text-xs text-[#8A8A8A] leading-relaxed border-t border-[#8B1A1A]/10 pt-3 mt-2">
          {getEventContextSubtitle()}
        </p>
        
        {matchReasons.length > 0 && (
          <ul className="mt-3 space-y-1">
            {matchReasons.slice(0, 3).map((reason, idx) => (
              <li key={idx} className="text-xs text-[#8A8A8A] flex items-center gap-2">
                <span className="w-1 h-1 bg-[#8B1A1A] rounded-full"></span>
                {reason}
              </li>
            ))}
          </ul>
        )}
      </div>
      
      {/* Event Details */}
      <div className="space-y-2 text-sm text-[#8A8A8A] mb-4">
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-[#8B1A1A]" />
          {eventDate.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        {event.start_time && (
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-[#8B1A1A]" />
            {event.start_time} {event.end_time && `- ${event.end_time}`}
          </div>
        )}
        <div className="flex items-center gap-2">
          <MapPin size={14} className="text-[#8B1A1A]" />
          {event.venue_name}, {event.city}
        </div>
      </div>
      
      {/* Capacity Indicator */}
      <div className="flex items-center justify-between text-xs mb-5 p-2 bg-[#111]/50 border border-[#8B1A1A]/10">
        <span className="text-[#8A8A8A] flex items-center gap-1">
          <Users size={12} className="text-[#8B1A1A]" />
          Cupo del evento
        </span>
        <span className={`font-medium ${capacityRemaining <= 3 ? 'text-[#8B1A1A]' : 'text-[#C4A76B]'}`}>
          {capacityRemaining > 0 
            ? `${capacityRemaining} lugar${capacityRemaining !== 1 ? 'es' : ''} disponible${capacityRemaining !== 1 ? 's' : ''}`
            : 'Último lugar'
          }
        </span>
      </div>

      {/* Invited timestamp */}
      {event.invited_at && (
        <p className="text-xs text-[#8A8A8A]/60 mb-4">
          Invitado el {new Date(event.invited_at).toLocaleDateString('es-ES', { 
            day: 'numeric', 
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
      )}

      {/* RSVP Buttons */}
      <div className="flex gap-3">
        <button
          onClick={() => onRSVP(event.event_id, 'accepted')}
          disabled={loading || capacityRemaining <= 0}
          className={`flex-1 btn-wine flex items-center justify-center gap-2 ${
            capacityRemaining <= 0 ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          data-testid={`accept-${event.event_id}`}
        >
          {loading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <>
              <CheckCircle size={18} />
              {t('dashboard.events.accept') || 'Aceptar'}
            </>
          )}
        </button>
        <button
          onClick={() => onRSVP(event.event_id, 'declined')}
          disabled={loading}
          className="flex-1 border border-[#8A8A8A]/30 text-[#8A8A8A] px-4 py-2 hover:border-red-500/50 hover:text-red-400 transition-colors flex items-center justify-center gap-2"
          data-testid={`decline-${event.event_id}`}
        >
          <XCircle size={18} />
          {t('dashboard.events.decline') || 'Rechazar'}
        </button>
      </div>
    </div>
  );
};

const EventCard = ({ event, confirmed, t }) => {
  const eventDate = new Date(event.date);
  const [warmup, setWarmup] = useState(null);

  useEffect(() => {
    if (confirmed && ['inviting', 'confirmed'].includes(event.status)) {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/events/${event.event_id}/warmup`, { withCredentials: true })
        .then(res => { if (res.data.warmup) setWarmup(res.data.warmup); })
        .catch(() => {});
    }
  }, [event.event_id, confirmed, event.status]);

  return (
    <div className="card-premium p-6 group" data-testid={`event-${event.event_id}`}>
      <div className="flex items-center gap-2 mb-4">
        <span className="label-premium">{event.event_type}</span>
        {confirmed && (
          <span className="text-xs px-2 py-1 bg-emerald-900/30 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
            <CheckCircle size={12} />
            {t('dashboard.events.confirmed_badge') || 'Confirmado'}
          </span>
        )}
        {!confirmed && (
          <span className={`text-xs px-2 py-1 ${
            event.status === 'completed' ? 'bg-[#8A8A8A]/20 text-[#8A8A8A]' :
            event.status === 'cancelled' ? 'bg-red-900/20 text-red-400' :
            'bg-[#8B1A1A]/20 text-[#8B1A1A]'
          }`}>
            {event.status}
          </span>
        )}
      </div>
      <h3 className="text-xl font-serif text-[#F5F0E6] mb-3 group-hover:text-[#8B1A1A] transition-colors">
        {event.title}
      </h3>
      <p className="text-[#8A8A8A] text-sm mb-5 line-clamp-2">{event.description}</p>

      {/* Warm-up pre-event hint */}
      {warmup && (
        <div className="mb-5 px-4 py-3 bg-[#8B1A1A]/5 border border-[#8B1A1A]/15" data-testid="event-warmup">
          <p className="text-[#F5F0E6] text-sm italic">{warmup.primary}</p>
          {warmup.secondary && (
            <p className="text-[#C4A76B] text-xs mt-1 italic">{warmup.secondary}</p>
          )}
        </div>
      )}

      <div className="space-y-2 text-sm text-[#8A8A8A]">
        <div className="flex items-center gap-2">
          <Calendar size={14} className="text-[#8B1A1A]" />
          {eventDate.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
        </div>
        {event.start_time && (
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-[#8B1A1A]" />
            {event.start_time}
          </div>
        )}
        <div className="flex items-center gap-2">
          <MapPin size={14} className="text-[#8B1A1A]" />
          {event.venue_name}, {event.city}
        </div>
      </div>
    </div>
  );
};

// ============== MATCHES TAB ==============
const MatchesTab = ({ matches, t }) => {
  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-serif text-[#F5F0E6] mb-3">
          {t('dashboard.matches.title') || 'Mis Matches'}
        </h2>
        <p className="text-[#8A8A8A]">
          {t('dashboard.matches.subtitle') || 'Conexiones mutuas creadas después de experiencias presenciales.'}
        </p>
      </div>

      {matches.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {matches.map((match, i) => (
            <MatchCard key={match.match?.match_id || i} match={match} t={t} />
          ))}
        </div>
      ) : (
        <div className="card-premium p-16 text-center">
          <Heart className="w-16 h-16 text-[#8B1A1A] mx-auto mb-6 opacity-50" />
          <h3 className="text-2xl font-serif text-[#F5F0E6] mb-4">
            {t('dashboard.matches.empty_title') || 'Sin Matches Aún'}
          </h3>
          <p className="text-[#8A8A8A] max-w-md mx-auto mb-6">
            {t('dashboard.matches.empty_message') || 'Los matches surgen después de experiencias presenciales. Cuando ambas personas se seleccionan mutuamente, el match se revela.'}
          </p>
          <div className="inline-flex items-center gap-2 text-[#8B1A1A] text-sm">
            <Sparkles size={16} />
            {t('dashboard.matches.philosophy') || 'Las conexiones reales suceden en persona'}
          </div>
        </div>
      )}
    </div>
  );
};

const MatchCard = ({ match, t }) => {
  const matchedUser = match.user;
  const matchData = match.match;

  return (
    <div className="card-premium p-6" data-testid={`match-${matchData?.match_id}`}>
      <div className="flex items-center gap-4 mb-5">
        {matchedUser?.picture ? (
          <img 
            src={matchedUser.picture} 
            alt={matchedUser.name}
            className="w-16 h-16 rounded-full border-2 border-[#8B1A1A]/50"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-[#161616] border-2 border-[#8B1A1A]/50 flex items-center justify-center">
            <span className="text-[#8B1A1A] text-xl font-serif">
              {matchedUser?.name?.charAt(0)?.toUpperCase()}
            </span>
          </div>
        )}
        <div>
          <h3 className="text-lg font-serif text-[#F5F0E6]">{matchedUser?.name}</h3>
          <div className="flex items-center gap-1 text-emerald-400 text-sm">
            <Heart size={14} fill="currentColor" />
            {t('dashboard.matches.mutual') || 'Match mutuo'}
          </div>
        </div>
      </div>

      {match.event && (
        <p className="text-[#8A8A8A] text-sm mb-4">
          {t('dashboard.matches.met_at') || 'Se conocieron en'}: {match.event.title}
        </p>
      )}

      <button className="btn-wine w-full flex items-center justify-center gap-2">
        <MessageCircle size={16} />
        {t('dashboard.matches.start_chat') || 'Iniciar Conversación'}
      </button>
    </div>
  );
};

// ============== PROFILE TAB ==============
const ProfileTab = ({ user, questionnaire, t }) => {
  return (
    <div className="max-w-2xl">
      <div className="mb-8">
        <h2 className="text-2xl font-serif text-[#F5F0E6] mb-3">
          {t('dashboard.profile.title') || 'Mi Perfil'}
        </h2>
        <p className="text-[#8A8A8A]">
          {t('dashboard.profile.subtitle') || 'Información de tu perfil en Soul Mate Dates.'}
        </p>
      </div>

      <div className="card-premium p-8">
        {/* Avatar and basic info */}
        <div className="flex items-center gap-6 mb-8 pb-8 border-b border-[#8B1A1A]/10">
          {user?.picture ? (
            <img 
              src={user.picture} 
              alt={user.name}
              className="w-24 h-24 rounded-full border-2 border-[#8B1A1A]/50"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-[#161616] border-2 border-[#8B1A1A]/50 flex items-center justify-center">
              <span className="text-[#8B1A1A] text-3xl font-serif">
                {user?.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
          )}
          <div>
            <h3 className="text-2xl font-serif text-[#F5F0E6]">{user?.name}</h3>
            <p className="text-[#8A8A8A]">{user?.email}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="px-3 py-1 bg-emerald-900/30 text-emerald-400 text-xs border border-emerald-500/30">
                {t('dashboard.profile.approved') || 'Aprobado'}
              </span>
              <span className="px-3 py-1 bg-[#8B1A1A]/20 text-[#8B1A1A] text-xs border border-[#8B1A1A]/30">
                {user?.membership_tier || 'free'}
              </span>
            </div>
          </div>
        </div>

        {/* Profile details */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-[#8A8A8A] text-sm mb-1">{t('dashboard.profile.age') || 'Edad'}</p>
            <p className="text-[#F5F0E6]">{questionnaire?.basic_info?.age || user?.age || '-'} años</p>
          </div>
          <div>
            <p className="text-[#8A8A8A] text-sm mb-1">{t('dashboard.profile.city') || 'Ciudad'}</p>
            <p className="text-[#F5F0E6]">{questionnaire?.basic_info?.city || user?.city || '-'}</p>
          </div>
          <div>
            <p className="text-[#8A8A8A] text-sm mb-1">{t('dashboard.profile.occupation') || 'Ocupación'}</p>
            <p className="text-[#F5F0E6]">{questionnaire?.basic_info?.occupation || user?.occupation || '-'}</p>
          </div>
          <div>
            <p className="text-[#8A8A8A] text-sm mb-1">{t('dashboard.profile.intention') || 'Intención'}</p>
            <p className="text-[#F5F0E6]">{questionnaire?.intention?.looking_for || '-'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== EXPERIENCE TAB ==============
const ExperienceTab = ({ user, t }) => {
  return (
    <div className="max-w-2xl">
      <div className="mb-8">
        <h2 className="text-2xl font-serif text-[#F5F0E6] mb-3">
          {t('dashboard.experience.title') || 'Mi Experiencia'}
        </h2>
        <p className="text-[#8A8A8A]">
          {t('dashboard.experience.subtitle') || 'Tu journey en Soul Mate Dates.'}
        </p>
      </div>

      <div className="card-premium p-8 mb-8">
        <h3 className="text-lg font-serif text-[#F5F0E6] mb-4">
          {t('dashboard.experience.philosophy_title') || 'Nuestra Filosofía'}
        </h3>
        <p className="text-[#8A8A8A] mb-6">
          {t('dashboard.experience.philosophy_text') || 'Soul Mate Dates no es una app de citas convencional. Creemos que las conexiones profundas suceden en persona, en espacios cuidadosamente diseñados para ello.'}
        </p>
        <ul className="space-y-4">
          <li className="flex items-start gap-3 text-[#8A8A8A]">
            <Shield size={18} className="text-[#8B1A1A] mt-0.5 flex-shrink-0" />
            {t('dashboard.experience.point_1') || 'Grupos pequeños de alta compatibilidad'}
          </li>
          <li className="flex items-start gap-3 text-[#8A8A8A]">
            <Star size={18} className="text-[#8B1A1A] mt-0.5 flex-shrink-0" />
            {t('dashboard.experience.point_2') || 'Eventos premium en locaciones exclusivas'}
          </li>
          <li className="flex items-start gap-3 text-[#8A8A8A]">
            <Heart size={18} className="text-[#8B1A1A] mt-0.5 flex-shrink-0" />
            {t('dashboard.experience.point_3') || 'Chat solo después de conexión mutua post-evento'}
          </li>
        </ul>
      </div>

      <div className="card-premium p-8">
        <h3 className="text-lg font-serif text-[#F5F0E6] mb-4">
          {t('dashboard.experience.status_title') || 'Tu Estado'}
        </h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="p-4 bg-[#0B0B0B] border border-[#8B1A1A]/10">
            <p className="text-[#8A8A8A] text-sm mb-1">{t('dashboard.experience.events_attended') || 'Eventos asistidos'}</p>
            <p className="text-2xl font-serif text-[#8B1A1A]">0</p>
          </div>
          <div className="p-4 bg-[#0B0B0B] border border-[#8B1A1A]/10">
            <p className="text-[#8A8A8A] text-sm mb-1">{t('dashboard.experience.matches_made') || 'Matches creados'}</p>
            <p className="text-2xl font-serif text-[#8B1A1A]">0</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
